import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CreditCard, Calendar, DollarSign, AlertCircle, Edit, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Conta, FaturaCartao } from "@/types/database";
import { InvoiceManager } from "@/components/management/InvoiceManager";
import { CreditCardForm } from "@/components/forms/CreditCardForm";
import { CreditCardEditForm } from "@/components/forms/CreditCardEditForm";
import { getInvoiceStatus } from "@/lib/dateUtils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useCreditCardOperations } from "@/hooks/useCreditCardOperations";
import { EmptyState } from "../ui/EmptyState";
import { useLocation, useNavigate } from "react-router-dom";

export const CreditCardManager: React.FC = () => {
  const [creditCards, setCreditCards] = useState<Conta[]>([]);
  const [invoices, setInvoices] = useState<Record<string, FaturaCartao>>({});
  const [selectedCard, setSelectedCard] = useState<Conta | null>(null);
  const [loading, setLoading] = useState(true);
  const [cardToDelete, setCardToDelete] = useState<Conta | null>(null);
  const [editingCard, setEditingCard] = useState<Conta | null>(null);
  const [targetDate, setTargetDate] = useState<string | undefined>();
  const [targetInvoiceId, setTargetInvoiceId] = useState<string | undefined>();
  const location = useLocation();
  const navigate = useNavigate();
  const handledNavigationCardIdRef = useRef<string | null>(null);

  useEffect(() => {
    fetchCreditCards();
  }, []);

  // Seleciona automaticamente o cartão se o ID vier pelo navigation state
  useEffect(() => {
    const state = location.state as { cardId?: string, targetDate?: string, invoiceId?: string } | null;
    const navCardId = state?.cardId;

    if (navCardId && handledNavigationCardIdRef.current !== navCardId && creditCards.length > 0 && !selectedCard) {
      const card = creditCards.find(c => c.id === navCardId);
      if (card) {
        handledNavigationCardIdRef.current = navCardId;
        setSelectedCard(card);
        if (state?.targetDate) {
          setTargetDate(state.targetDate);
        }
        if (state?.invoiceId) {
          setTargetInvoiceId(state.invoiceId);
        }
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
      }
    }
  }, [creditCards, location.pathname, location.search, location.state, navigate, selectedCard]);

  const fetchCreditCards = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("contas")
        .select("*")
        .eq("tipo", "cartao_credito")
        .eq("ativo", true)
        .order("nome");

      if (error) {
        console.error("Erro ao buscar cartões:", error);
        return;
      }

      // Calcular o limite comprometido para cada cartão (soma de todas as faturas não pagas)
      const cardsWithAvailableLimit = await Promise.all((data || []).map(async (card) => {
        const { data: unpaidInvoices } = await supabase
          .from("faturas_cartao")
          .select("valor_total")
          .eq("cartao_id", card.id)
          .eq("paga", false);

        const totalCommitted = unpaidInvoices?.reduce((sum, invoice) => sum + Number(invoice.valor_total), 0) || 0;

        return {
          ...card,
          limite_comprometido: totalCommitted
        };
      }));

      setCreditCards(cardsWithAvailableLimit as Conta[]);

      // Fetch next unpaid invoice for each card
      const { data: invoicesData } = await supabase
        .from("faturas_cartao")
        .select("*")
        .eq("paga", false)
        .order("data_vencimento", { ascending: true });

      const invoicesMap: Record<string, FaturaCartao> = {};
      // Get the earliest unpaid invoice for each card
      invoicesData?.forEach((invoice) => {
        if (!invoicesMap[invoice.cartao_id]) {
          invoicesMap[invoice.cartao_id] = invoice;
        }
      });
      setInvoices(invoicesMap);
    } catch (error) {
      console.error("Erro inesperado:", error);
      toast.error("Erro ao carregar cartões de crédito.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return "bg-destructive";
    if (percentage >= 70) return "bg-warning";
    return "bg-primary";
  };

  const handleDeleteCard = async (card: Conta) => {
    try {
      const { error } = await supabase
        .from("contas")
        .delete()
        .eq("id", card.id);

      if (error) {
        console.error("Erro ao excluir cartão:", error);
        toast.error("Erro ao excluir cartão de crédito.");
        return;
      }

      toast.success("Cartão de crédito excluído com sucesso!");
      setCardToDelete(null);
      fetchCreditCards();
    } catch (error) {
      console.error("Erro inesperado:", error);
      toast.error("Erro inesperado ao excluir cartão.");
    }
  };

  const handleEditCard = (card: Conta) => {
    setEditingCard(card);
  };

  if (selectedCard) {
    return (
      <InvoiceManager
        card={selectedCard}
        onBack={() => {
          setSelectedCard(null);
          setTargetDate(undefined);
          setTargetInvoiceId(undefined);
        }}
        onSuccess={fetchCreditCards}
        targetDate={targetDate}
        targetInvoiceId={targetInvoiceId}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-4">
          <CardTitle>Cartões de Crédito</CardTitle>
          <CreditCardForm onSuccess={fetchCreditCards} />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse border rounded-lg p-4">
                <div className="space-y-2 mb-4">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
                <div className="space-y-3">
                  <div className="h-3 bg-muted rounded"></div>
                  <div className="h-8 bg-muted rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : creditCards.length === 0 ? (
          <EmptyState
            icon={CreditCard}
            title="Nenhum cartão"
            description="Você ainda não cadastrou nenhum cartão de crédito. Adicione um para controlar suas faturas e limites."
            className="border rounded-lg"
          >
            <CreditCardForm onSuccess={fetchCreditCards} />
          </EmptyState>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {creditCards.map((card) => {
              const currentInvoice = invoices[card.id];
              const faturaAtual = card.fatura_atual || 0;
              const limiteComprometido = (card as Conta & { limite_comprometido?: number }).limite_comprometido || 0; // Total de todas as faturas não pagas
              const limite = card.limite_credito || 0;
              const disponivel = limite - limiteComprometido; // Limite disponível para novas compras
              const usagePercentage = getUsagePercentage(limiteComprometido, limite);

              return (
                <div
                  key={card.id}
                  className="relative bg-card border border-border rounded-lg p-4 hover:bg-accent/30 hover:shadow-medium transition-all duration-300 shadow-soft"
                >
                  {/* Action buttons */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            aria-label={`Editar cartão ${card.nome}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCard(card);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Editar cartão</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            aria-label={`Excluir cartão ${card.nome}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCardToDelete(card);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Excluir cartão</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  {/* Card content - clickable area */}
                  <div
                    className="cursor-pointer"
                    onClick={() => setSelectedCard(card)}
                  >
                    <div className="pb-3 pr-16"> {/* Added right padding to avoid overlap with buttons */}
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-semibold flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: card.cor }}
                          />
                          {card.nome}
                        </h3>
                        {usagePercentage >= 90 && (
                          <AlertCircle className="h-4 w-4 text-destructive" />
                        )}
                      </div>
                      {card.instituicao_nome && (
                        <p className="text-sm text-muted-foreground">
                          {card.instituicao_nome}
                        </p>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Fatura Atual</span>
                          <span className="font-medium">
                            {formatCurrency(faturaAtual)}
                          </span>
                        </div>
                        <Progress
                          value={usagePercentage}
                          className="h-2"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Disponível: {formatCurrency(disponivel)}</span>
                          <span>Limite: {formatCurrency(limite)}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Fecha dia {card.dia_fechamento || 'N/A'}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Vence dia {card.dia_vencimento}
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          {card.bandeira && (
                            <div className="text-xs text-muted-foreground font-medium">
                              {card.bandeira}
                            </div>
                          )}
                          {(() => {
                            const status = currentInvoice ? getInvoiceStatus(currentInvoice, card) : getInvoiceStatus({ 
                              paga: false, 
                              mes_referencia: new Date().getMonth() + 1, 
                              ano_referencia: new Date().getFullYear() 
                            }, card);
                            const statusConfig = {
                              paga: { label: "Paga", variant: "default" as const, className: "bg-green-500 hover:bg-green-600" },
                              aberta: { label: "Aberta", variant: "secondary" as const, className: "bg-blue-500 text-white hover:bg-blue-600" },
                              fechada: { label: "Fechada", variant: "destructive" as const, className: "bg-orange-500 hover:bg-orange-600 border-none" },
                              parcial: { label: "Parcial", variant: "outline" as const, className: "text-muted-foreground" },
                            };
                            const config = statusConfig[status];
                            return (
                              <Badge variant={config.variant} className={config.className}>
                                {config.label}
                              </Badge>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div> {/* Closing div for clickable area */}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!cardToDelete} onOpenChange={() => setCardToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Cartão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cartão "{cardToDelete?.nome}"?
              Esta ação não pode ser desfeita e todas as faturas associadas também serão removidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cardToDelete && handleDeleteCard(cardToDelete)}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit dialog - now using the proper edit form */}
      <CreditCardEditForm
        card={editingCard}
        isOpen={!!editingCard}
        onClose={() => setEditingCard(null)}
        onSuccess={fetchCreditCards}
      />
    </Card>
  );
};
